import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import Button from 'carbon-react/lib/components/button';
import PopoverContainer, { RenderOpenProps } from 'carbon-react/lib/components/popover-container';

import { getWorkspaceListingUrl } from '@futrli/predict-next/workspaces/utils/route-utils';
import { matchConsolidationDetailRoutePath, useModal } from '@futrli/shared-services';
import { useSidebarContext } from '@futrli/shared-services/contexts/sidebar-context/useSidebarContext';

import styles from './create-entity-menu.scss';

interface CreateMenuItemProps {
  itemId: string;
  onClick: (itemId: string) => void;
  children: React.ReactNode;
}

const CREATE_PL_BUDGET_ID = 'pl-budget';
const CREATE_3_WAY_FORECAST_ID = '3-way-forecast';
const CREATE_REPORT_ID = 'report';
const CREATE_REPORT_TEMPLATE_ID = 'report-template';
const CREATE_DASHBOARD_ID = 'dashboard';
const CREATE_DASHBOARD_TEMPLATE_ID = 'dashboard-template';
const CREATE_NF_DRIVER_ID = 'nf-driver';
const CREATE_CONSOLIDATION_ID = 'consolidation';

const CreateMenuItem = ({ itemId, onClick, children }: CreateMenuItemProps) => (
  <div className={styles.createMenuItem} onClick={() => onClick(itemId)} role="button" tabIndex={0}>
    {children}
  </div>
);

export const CreateEntityMenu = () => {
  const isConsolidation = !!matchConsolidationDetailRoutePath(window.location.pathname);
  const intl = useIntl();

  const navigate = useNavigate();
  const { workspaceId = '' } = useParams();

  const {
    pAndLBudget,
    report,
    template,
    threeWayForecast,
    dashboard,
    dashboardTemplate,
    driverBuilder
  } = useSidebarContext();
  const { open: openBudgetSidebar } = pAndLBudget;
  const { open: openReportSidebar } = report;
  const { open: openTemplateSidebar } = template;
  const { open: openThreeWayForecastSidebar } = threeWayForecast;
  const { open: openDashboardSidebar } = dashboard;
  const { open: openDashboardTemplateSidebar } = dashboardTemplate;
  const { open: openDriverBuilderSidebar } = driverBuilder;

  const { isOpen, open, close } = useModal();

  const handleItemClick = (itemId: string) => {
    switch (itemId) {
      case CREATE_PL_BUDGET_ID:
        openBudgetSidebar();
        break;
      case CREATE_3_WAY_FORECAST_ID:
        openThreeWayForecastSidebar();
        break;
      case CREATE_REPORT_ID:
        openReportSidebar();
        break;
      case CREATE_REPORT_TEMPLATE_ID:
        openTemplateSidebar();
        break;
      case CREATE_DASHBOARD_ID:
        openDashboardSidebar();
        break;
      case CREATE_DASHBOARD_TEMPLATE_ID:
        openDashboardTemplateSidebar();
        break;
      case CREATE_NF_DRIVER_ID:
        openDriverBuilderSidebar({});
        break;
      case CREATE_CONSOLIDATION_ID:
        navigate(getWorkspaceListingUrl(workspaceId, 'consolidations'));
        break;
      default:
        return;
    }
    close();
  };

  return (
    <>
      <div className={styles.createMenuWrapper}>
        <PopoverContainer
          p={0}
          open={isOpen}
          onOpen={open}
          onClose={close}
          position="left"
          containerAriaLabel={intl.formatMessage({ id: 'common.create_entity', defaultMessage: 'Create entity' })}
          ariaDescribedBy="create-menu-popover"
          renderOpenComponent={({
            id,
            ref,
            onClick,
            'data-element': dataElement,
            'aria-label': ariaLabel,
            'aria-expanded': ariaExpanded,
            'aria-haspopup': ariaHasPopup
          }: RenderOpenProps) => (
            <Button
              buttonType="secondary"
              size="small"
              iconType="add"
              iconPosition="after"
              onClick={onClick}
              ref={ref}
              id={id}
              data-element={dataElement}
              aria-label={ariaLabel}
              aria-expanded={ariaExpanded}
              aria-haspopup={ariaHasPopup}
            >
              <FormattedMessage id="common.create" defaultMessage="Create" />
            </Button>
          )}
          renderCloseComponent={() => <></>}
        >
          <div className={styles.createMenu} id="create-menu-popover">
            <div className={styles.createMenuHeaderRow}>
              <div className={styles.createMenuColumnHeader}>
                <FormattedMessage id="reporting.forecasts" defaultMessage="Forecasts" />
              </div>
              <div className={styles.createMenuColumnHeader}>
                <FormattedMessage id="reporting.reports" defaultMessage="Reports" />
              </div>
              {!isConsolidation && (
                <div className={styles.createMenuColumnHeader}>
                  <FormattedMessage id="predict.dashboards" defaultMessage="Dashboards" />
                </div>
              )}
              <div className={styles.createMenuColumnHeader}>
                <FormattedMessage id="predict.common.other_entity_create" defaultMessage="Other" />
              </div>
            </div>

            <div className={styles.createMenuDivider} />

            <div className={styles.createMenuItemsRow}>
              {/* Forecasts column */}
              <div className={styles.createMenuColumn}>
                <CreateMenuItem itemId={CREATE_PL_BUDGET_ID} onClick={handleItemClick}>
                  <FormattedMessage id="predict.create_forecast.p_l_budget_v2" defaultMessage="P&L budget" />
                </CreateMenuItem>
                <CreateMenuItem itemId={CREATE_3_WAY_FORECAST_ID} onClick={handleItemClick}>
                  <FormattedMessage id="predict.create.3_way_forecast_v2" defaultMessage="3-way forecast" />
                </CreateMenuItem>
              </div>

              {/* Reports column */}
              <div className={styles.createMenuColumn}>
                <CreateMenuItem itemId={CREATE_REPORT_ID} onClick={handleItemClick}>
                  <FormattedMessage id="reporting.report" defaultMessage="Report" />
                </CreateMenuItem>
                <CreateMenuItem itemId={CREATE_REPORT_TEMPLATE_ID} onClick={handleItemClick}>
                  <FormattedMessage id="predict.create.report_template_v2" defaultMessage="Report template" />
                </CreateMenuItem>
              </div>

              {!isConsolidation && (
                <div className={styles.createMenuColumn}>
                  <CreateMenuItem itemId={CREATE_DASHBOARD_ID} onClick={handleItemClick}>
                    <FormattedMessage id="reporting.dashboard" defaultMessage="Dashboard" />
                  </CreateMenuItem>
                  <CreateMenuItem itemId={CREATE_DASHBOARD_TEMPLATE_ID} onClick={handleItemClick}>
                    <FormattedMessage id="predict.create.dashboard_template_v2" defaultMessage="Dashboard template" />
                  </CreateMenuItem>
                </div>
              )}

              {/* Other column */}
              <div className={styles.createMenuColumn}>
                {!isConsolidation && (
                  <CreateMenuItem itemId={CREATE_NF_DRIVER_ID} onClick={handleItemClick}>
                    <FormattedMessage
                      id="predict.create.non_financial_driver_v2"
                      defaultMessage="Non-financial driver"
                    />
                  </CreateMenuItem>
                )}
                <CreateMenuItem itemId={CREATE_CONSOLIDATION_ID} onClick={handleItemClick}>
                  <FormattedMessage id="predict.consolidation" defaultMessage="Consolidation" />
                </CreateMenuItem>
              </div>
            </div>
          </div>
        </PopoverContainer>
      </div>
    </>
  );
};
